import React from "react";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";

import { getFileServerImageAddress } from "@components/FileServer/Img";

const COMPACT_BANNER_MEDIA = "(max-width: 1009px)";

type LocalizedValue = Record<string, string | undefined>;

const getLocalizedValue = (
  values: LocalizedValue | undefined,
  language: string | null
): string | undefined => {
  const languageCode = language === "cs" ? "cz" : language;
  return (
    (languageCode && values?.[languageCode]) || values?.default || undefined
  );
};

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

const FlightSearchResultsBanner: React.FC = () => {
  const { banner, currentLanguage, fileServerData } = useSelector(
    (state: AppState) => ({
      banner:
        state.storage.frontendSettings?.dealerFrontendSettings
          ?.flightSearchResultsBanner,
      currentLanguage: state.requestorConfig.currentLanguage,
      fileServerData: state.requestorConfig.fileServerData,
    })
  );

  const wideImage = getLocalizedValue(banner?.images?.wide, currentLanguage);
  const compactImage = getLocalizedValue(
    banner?.images?.compact,
    currentLanguage
  );
  const targetUrl = getLocalizedValue(banner?.targetUrl, currentLanguage);
  const altText = getLocalizedValue(banner?.altText, currentLanguage);

  if (
    !wideImage ||
    !compactImage ||
    !targetUrl ||
    !altText ||
    !isHttpsUrl(targetUrl)
  ) {
    return null;
  }

  return (
    <a
      className="flight-search-results-banner"
      data-test-id="flight-search-results-banner"
      href={targetUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <picture>
        <source
          media={COMPACT_BANNER_MEDIA}
          srcSet={getFileServerImageAddress(compactImage, fileServerData)}
        />
        <img
          src={getFileServerImageAddress(wideImage, fileServerData)}
          alt={altText}
          loading="lazy"
        />
      </picture>
    </a>
  );
};

export default FlightSearchResultsBanner;
